import {
  Button,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatCurrency,
  formatDate,
  titleCase,
  toast,
  useBreakpoint,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { PeriodFilter } from '@/app/period-filter'
import { Page } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import {
  useRestoreTreatmentPlanRow,
  useTreatmentPlans,
  type TreatmentPlanFilter,
  type TreatmentPlanRow,
} from './api'
import {
  CRITERION,
  EMPTY_LIST,
  EMPTY_PENDING,
  FILTERS,
  FILTER_LABELS,
  FOOTER_NOTE,
  ORDER_NOTES,
  archivedLine,
  subtitle,
} from './labels'

/**
 * Planos de tratamento (docs/areas/planos-de-tratamento.md, "Lista global"; RN-PLA-36). Arquétipo:
 * lista de trabalho. A pergunta: "onde falta prova?". Abre em Ativos, com pendência primeiro; a
 * coluna PENDÊNCIA é a razão de existir da tela. "Arquivados" só para gestora e root (RN-PLA-33).
 */

export function TreatmentPlansPage() {
  const navigate = useNavigate()
  const session = useSession()
  const isDesk = useBreakpoint('desk')
  const canSeeArchived = session.canManageClinic
  const allowed = canSeeArchived ? FILTERS : FILTERS.filter((f) => f !== 'archived')
  const [params, setParams] = useSearchParams()
  const stored = useStoredFilter('treatment-plans', allowed, 'open')
  const urlFilter = params.get('filter')
  const filter: TreatmentPlanFilter = (allowed as readonly string[]).includes(urlFilter ?? '')
    ? (urlFilter as TreatmentPlanFilter)
    : stored.filter
  const [period, setPeriod] = useState<{ from: string; to: string }>({ from: '', to: '' })
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const term = useDebounced(search.trim())
  const query = useTreatmentPlans({
    filter,
    from: period.from || undefined,
    to: period.to || undefined,
    search: term.length >= 2 ? term.slice(0, 80) : undefined,
    page,
  })
  const restore = useRestoreTreatmentPlanRow()
  const meta = query.data?.meta
  const rows = query.data?.data ?? []

  function setFilter(next: TreatmentPlanFilter) {
    stored.setFilter(next)
    setPage(1)
    const nextParams = new URLSearchParams(params)
    nextParams.delete('filter')
    setParams(nextParams, { replace: true })
  }

  const options: FilterSegmentOption<TreatmentPlanFilter>[] = allowed.map((value) => ({
    value,
    label: FILTER_LABELS[value],
    count: value === 'archived' ? meta?.counts.archived : meta?.counts[value],
  }))
  const hasNoPlans = !query.isPending && !term && (meta?.counts.all ?? 0) === 0
  const firstColumn =
    filter === 'closed' ? 'Encerrado' : filter === 'archived' ? 'Arquivado' : 'Criado'

  function firstCell(row: TreatmentPlanRow) {
    if (filter === 'archived') return archivedLine(row)
    const at = filter === 'closed' && row.closedAt ? row.closedAt : row.createdAt
    return formatDate(new Date(at), { year: false })
  }

  function restoreRow(row: TreatmentPlanRow) {
    restore.mutate(
      { id: row.id, version: row.version },
      {
        onSuccess: () => toast(`Plano de ${titleCase(row.patient.name)} restaurado.`),
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  const empty = term ? (
    <EmptyState
      bordered
      title={`Nenhum plano para "${term}"`}
      description="A busca cobre o nome da paciente, o procedimento e o lote, dentro do filtro e do período."
      action={
        <Button variant="outline" onClick={() => setSearch('')}>
          Limpar busca
        </Button>
      }
    />
  ) : filter === 'pending' ? (
    <EmptyState
      bordered
      title={EMPTY_PENDING.title}
      description={EMPTY_PENDING.description}
      action={
        <Button variant="outline" onClick={() => setFilter('all')}>
          Ver todos
        </Button>
      }
    />
  ) : hasNoPlans && filter === 'all' ? (
    <EmptyState
      bordered
      title={EMPTY_LIST.title}
      description={EMPTY_LIST.description}
      action={
        <Button variant="outline" asChild>
          <Link to={routes.newTreatmentPlan()}>+ Novo plano</Link>
        </Button>
      }
    />
  ) : (
    <EmptyState
      bordered
      title={
        filter === 'open'
          ? 'Nenhum plano ativo'
          : filter === 'closed'
            ? 'Nenhum plano encerrado no período'
            : filter === 'archived'
              ? 'Nenhum plano arquivado'
              : 'Nenhum plano no período'
      }
      description={
        filter === 'archived'
          ? 'Planos arquivados saem das listas e do dashboard; voltam quando você restaurar.'
          : 'Mude o período ou o filtro para ver outros planos.'
      }
      action={
        filter !== 'all' ? (
          <Button variant="outline" onClick={() => setFilter('all')}>
            Ver todos
          </Button>
        ) : undefined
      }
    />
  )

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Operação' }, { label: 'Planos de Tratamento' }]}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to={routes.newTreatmentPlan()}>+ Novo plano</Link>
          </Button>
        }
      />
      <Page wide>
        <PageTitle title="Planos de tratamento" subtitle={subtitle(meta?.counts.all)} />
        <p className="font-body text-secondary mt-2 text-[12px] leading-[1.6] font-light">
          {CRITERION}
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <FilterSegments
            label="Filtrar planos"
            options={options}
            value={filter}
            onChange={setFilter}
          />
          <SearchInput
            className="tablet:max-w-[300px]"
            value={search}
            onChange={(value) => {
              setSearch(value)
              setPage(1)
            }}
            placeholder="Buscar por paciente, procedimento ou lote"
            aria-label="Buscar plano"
          />
        </div>
        <div className="mt-3">
          <PeriodFilter
            from={period.from}
            to={period.to}
            onApply={(from, to) => {
              setPeriod({ from, to })
              setPage(1)
            }}
            fromLabel={filter === 'closed' ? 'Encerrados a partir de' : 'Criados a partir de'}
            toLabel={filter === 'closed' ? 'Encerrados até' : 'Criados até'}
          />
        </div>

        <div className="mt-4">
          {query.isError ? (
            <LoadError
              title="Não conseguimos carregar os planos"
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : query.isPending ? (
            <TableSkeleton rows={6} columns={6} />
          ) : rows.length === 0 ? (
            empty
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <TableHead>{firstColumn}</TableHead>
                  <TableHead>Paciente</TableHead>
                  <TableHead>Procedimentos</TableHead>
                  <TableHead>Profissional</TableHead>
                  <TableHead numeric>Total</TableHead>
                  {filter === 'archived' ? (
                    <TableHead aria-label="Ações" />
                  ) : (
                    <TableHead>Pendência</TableHead>
                  )}
                </tr>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const name = titleCase(row.patient.name)
                  const hasPending = row.pendingItems.length > 0
                  const dateCell = (
                    <TableCell key="date" label={firstColumn} muted>
                      {firstCell(row)}
                    </TableCell>
                  )
                  const patientCell = (
                    <TableCell key="patient" primary>
                      {name}
                      {row.clinic ? (
                        <span className="ml-2 align-middle">
                          <StatusTag level="waiting">{row.clinic.name}</StatusTag>
                        </span>
                      ) : null}
                    </TableCell>
                  )
                  return (
                    <TableRow
                      key={row.id}
                      critical={hasPending}
                      onClick={() => navigate(routes.treatmentPlan(row.id))}
                    >
                      {isDesk ? [dateCell, patientCell] : [patientCell, dateCell]}
                      <TableCell label="Procedimentos" muted>
                        {row.procedures.join(', ')}
                      </TableCell>
                      <TableCell label="Profissional" muted>
                        {titleCase(row.practitioner.name)}
                      </TableCell>
                      <TableCell label="Total" numeric>
                        {formatCurrency(row.totalAmountCents / 100, { compact: true })}
                      </TableCell>
                      {filter === 'archived' ? (
                        <TableCell>
                          <RowActions>
                            <button
                              type="button"
                              disabled={restore.isPending}
                              onClick={() => restoreRow(row)}
                            >
                              restaurar
                            </button>
                            <button
                              type="button"
                              onClick={() => navigate(routes.treatmentPlan(row.id))}
                            >
                              abrir
                            </button>
                          </RowActions>
                        </TableCell>
                      ) : (
                        <TableCell label="Pendência">
                          {row.pendingItem ? (
                            <StatusTag level="attention">{row.pendingItem.label}</StatusTag>
                          ) : (
                            <span className="text-label">—</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
          {query.data && rows.length > 0 ? (
            <ListFooter
              meta={query.data.meta}
              noun={['plano', 'planos']}
              note={`${FOOTER_NOTE} ${ORDER_NOTES[filter]}.`}
              onPage={setPage}
            />
          ) : null}
        </div>
      </Page>
    </>
  )
}
